import { CircularProgress } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import * as Sentry from '@sentry/browser';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import axios from 'axios';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import appSettings from 'src/app-settings.json';
import { useComplaintForm } from 'src/app/context';
import { TStats } from 'src/types/types';

interface AddressOptionType {
  inputValue?: string;
  address: string;
  apn: string;
  value?: Record<string, string>;
}

interface IAutocompleteAddressProps {
  stats: TStats;
}

export default function AutocompleteAddress(props: IAutocompleteAddressProps): JSX.Element {
  const { stats } = props;

  const [value, setValue] = useState<AddressOptionType | null>(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);

  const debouncedAddressLookup = useRef(
    debounce(async (input: string) => {
      try {
        setLoading(true);
        setNoResults(false);
        const encodedInput = encodeURIComponent(input);
        const response = await axios.get(
          `${appSettings.strComplaintsApiDomain}${stats?.cityInfo?.addressLookup}${encodedInput}`
        );

        const results = Object.values(response.data) as [];
        setData(results);
        setNoResults(results.length === 0);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setNoResults(false);
        Sentry.captureException(err);
        Sentry.captureMessage('Error: Error fetching /lookupAddress');
      }
    }, 500)
  );

  const { updateAddress } = useComplaintForm();

  // When the component unmounts, cancel all debounced calls.
  useEffect(() => debouncedAddressLookup.current.cancel, []);

  useEffect(() => {
    if (value) updateAddress(value.address, value.apn);
  }, [updateAddress, value]);

  const statusMessage = (() => {
    if (loading) return 'Loading address suggestions';
    if (noResults) return 'No matching addresses found';
    if (data.length > 0)
      return `${data.length} address suggestion${data.length === 1 ? '' : 's'} available`;
    return '';
  })();

  return (
    <div>
      <Autocomplete
        id="autocomplete-address"
        options={data}
        value={value}
        onChange={(event, newValue) => {
          setNoResults(false);
          // Custom address text entered by user and pressing Enter
          if (typeof newValue === 'string') {
            setValue({
              address: newValue,
              apn: '-',
            });
          } else if (newValue && newValue.inputValue) {
            // Custom address entered by user and selecting the "Custom Address" option
            setValue({
              address: newValue.inputValue,
              apn: '-',
            });
          } else {
            // Regular option from list
            setValue(newValue);
          }
        }}
        loading={loading}
        onInputChange={(event, inputValue) => {
          if (inputValue.length < 3) {
            setData([]);
            setNoResults(false);
          }
          if (inputValue.length >= 3) {
            debouncedAddressLookup.current(inputValue);
          }
        }}
        getOptionLabel={(option) => {
          // Value selected from the input
          if (typeof option === 'string') {
            return option;
          }
          // Custom address entered by user
          if (option.inputValue) {
            return option.inputValue;
          }
          // Regular option from list
          return option.address;
        }}
        renderOption={(option, { inputValue }) => {
          const matches = match(option.address, inputValue);
          const parts = parse(option.address, matches);

          // Highlights search text in results for increased usability
          return (
            <div>
              {parts.map((part, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}
            </div>
          );
        }}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            variant="outlined"
            value={value}
            required
            error={noResults}
            helperText={noResults ? 'No matching addresses found' : undefined}
            FormHelperTextProps={noResults ? { role: 'alert' } : undefined}
            placeholder="Address"
            InputLabelProps={{ shrink: true, style: { display: 'none' } }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} aria-label="Loading address suggestions" />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        fullWidth
        freeSolo
      />
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        {statusMessage}
      </div>
    </div>
  );
}
